// What a connected service's setup page is built from (and a directory
// connection's, in identity/IntegrationsTab.jsx): the frame beside its aside,
// the tabs between its connections, the setup steps, and the pieces a step
// holds. The generic parts are the kit's (components/ui/), re-exported under
// the names these pages import.
import Button, { buttonClass } from "./ui/Button.jsx";
import Notice from "./ui/Notice.jsx";
import KitPanel from "./ui/Panel.jsx";
import KitTabs from "./ui/Tabs.jsx";
import { SkeletonBar } from "./ui/Skeleton.jsx";
import { Step as KitStep } from "./ui/Steps.jsx";
import { FIELD } from "./ui/styles.js";
import { PAGE_DESCRIPTION, PAGE_TITLE, PageStatus } from "./settings/Page.jsx";
import { SourceLogo, StatusPill, toneDotClass } from "./statusCard.jsx";

export { Steps } from "./ui/Steps.jsx";
export { CopyBlock, CopyField as CopyValue } from "./ui/Copy.jsx";
export { default as Field } from "./ui/Field.jsx";
export { ErrorText } from "./ui/Notice.jsx";

export const inputCls = `${FIELD} w-full`;

export const linkCls =
    "cursor-pointer border-0 bg-transparent p-0 text-[12px] text-[color:var(--green)] underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--green)]";

// A step that can't be worked on yet and has nothing to show keeps its title
// quiet.
export function Step({ state = "todo", children = null, ...props }) {
    return (
        <KitStep state={state} collapsed={state === "todo" && !children} {...props}>
            {children}
        </KitStep>
    );
}

// A box in ui/Panel's look whose caller sets its padding and layout: a list
// whose rows run to its edges (choices, projects, models).
export function Panel({ children, className = "", ...attrs }) {
    return (
        <div className={`rounded-[10px] border border-[color:var(--line)] bg-[color:var(--panel)] ${className}`} {...attrs}>
            {children}
        </div>
    );
}

// One of a few ways to do a step, picked with a radio.
export function MethodOption({ name, selected, title, description, onSelect, disabledReason = null }) {
    const disabled = Boolean(disabledReason);
    return (
        <label
            title={disabledReason ?? undefined}
            className={`flex items-start gap-[10px] rounded-[6px] px-[10px] py-[9px] transition-colors ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${selected ? "bg-[color-mix(in_srgb,var(--ink)_10%,transparent)]" : disabled ? "" : "hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)]"}`}
        >
            <input
                type="radio"
                name={name}
                checked={selected}
                onChange={onSelect}
                disabled={disabled}
                className="mt-[2px] shrink-0 accent-[color:var(--green)]"
            />
            <span className="grid min-w-0 gap-[2px]">
                <span className="text-[13px] font-medium leading-[1.35] text-[color:var(--ink)]">{title}</span>
                <span className="text-[12px] leading-[1.45] text-[color:var(--muted)]">{disabledReason ?? description}</span>
            </span>
        </label>
    );
}

// The permissions an app asks for, folded until opened.
export function PermissionList({ title, groups, note = null }) {
    return (
        <details className="rounded-[10px] border border-[color:var(--line)] bg-[color:var(--panel)] px-[14px] py-[10px]">
            <summary className="cursor-pointer text-[13px] font-medium leading-[1.4] text-[color:var(--ink)]">{title}</summary>
            <div className="mt-[10px] grid gap-[12px]">
                {groups.map((group, index) => (
                    <div key={group.caption ?? index} className="grid gap-[4px]">
                        {group.caption ? <span className="text-[12px] leading-[1.4] text-[color:var(--muted)]">{group.caption}</span> : null}
                        <ul className="m-0 grid list-none gap-[3px] p-0">
                            {group.permissions.map((permission) => (
                                <li key={permission.scope} className="flex flex-wrap items-baseline gap-x-[10px]">
                                    <code className="font-mono text-[12px] text-[color:var(--ink)]">{permission.scope}</code>
                                    <span className="text-[12px] text-[color:var(--muted)]">{permission.note}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                {note ? <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">{note}</p> : null}
            </div>
        </details>
    );
}

export function ConsentWait({ onCancel }) {
    return (
        <Notice
            tone="info"
            title="Waiting for admin consent in the new tab…"
            action={<Button size="sm" onClick={onCancel}>Cancel</Button>}
        />
    );
}

export function Instructions({ children }) {
    return (
        <ol className="m-0 grid list-decimal gap-[8px] pl-[18px] text-[12.5px] leading-[1.5] text-[color:var(--ink)] marker:text-[color:var(--muted)]">
            {children}
        </ol>
    );
}

// One titled part of a source page's aside.
export function AsideSection({ title, children }) {
    return (
        <div className="grid gap-[6px] border-t border-[color:var(--line-soft)] pt-[14px] first:border-t-0 first:pt-0">
            <h3 className="m-0 text-[13px] font-medium leading-[1.35] text-[color:var(--ink)]">{title}</h3>
            <div className="grid gap-[8px] text-[12.5px] leading-[1.5] text-[color:var(--muted)]">{children}</div>
        </div>
    );
}

// A source page whose data has not loaded: still loading (placeholders in
// the shape of its steps), or the load failed and can be retried in place.
export function SourceLoadState({ entry, onRetry }) {
    if (!entry?.loaded || entry.loading) {
        return (
            <div data-source-loading="">
                <span className="sr-only">Loading…</span>
                <div aria-hidden="true" className="grid gap-[26px]">
                    {[0, 1, 2].map((row) => (
                        <div key={row} className="grid grid-cols-[26px_minmax(0,1fr)] gap-x-[14px]" style={{ opacity: 1 - row * 0.25 }}>
                            <SkeletonBar className="h-[26px] w-[26px] rounded-full" />
                            <div className="grid content-start gap-[8px] pt-[5px]">
                                <SkeletonBar className="w-[46%]" />
                                <SkeletonBar className="w-[72%]" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return (
        <Notice tone="error" title="Couldn't load this service" action={<Button size="sm" onClick={onRetry}>Retry</Button>}>
            {entry.error || "Try again in a moment."}
        </Notice>
    );
}

// The frame every source page shares: its tabs and steps in a form-width
// column, beside an aside of what the connection does. In Settings the page's
// header already names the source, so the frame only puts its status there.
// Opened on its own (`onBack`), the frame draws the way back and the header.
export function SourcePage({ source, health, onBack, backLabel, tabs = null, aside, children, busy = false }) {
    return (
        <div className="@container min-w-0">
            <div className="grid items-start gap-[28px] @min-[880px]:grid-cols-[minmax(0,640px)_minmax(240px,300px)] @min-[880px]:justify-between">
                <div className="grid min-w-0 gap-[18px]">
                    {onBack ? (
                        <div className="grid gap-[10px]">
                            <button type="button" onClick={onBack} className={`${buttonClass("ghost", "sm")} w-fit pl-[4px]`}>
                                <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="h-[12px] w-[12px]">
                                    <path d="M10 3.5 5.5 8 10 12.5" />
                                </svg>
                                {backLabel}
                            </button>
                            <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[6px]">
                                <SourceLogo logo={source.logo} size={26} />
                                <h2 className={PAGE_TITLE}>{source.name}</h2>
                                {health ? <StatusPill tone={health.tone} label={health.label} /> : null}
                            </div>
                            <p className={PAGE_DESCRIPTION}>{source.blurb}</p>
                        </div>
                    ) : health ? (
                        <PageStatus>
                            <StatusPill tone={health.tone} label={health.label} />
                        </PageStatus>
                    ) : null}
                    {tabs}
                    <div aria-busy={busy ? "true" : undefined} className="min-w-0">
                        {children}
                    </div>
                </div>
                {aside ? <KitPanel as="aside">{aside}</KitPanel> : null}
            </div>
        </div>
    );
}

// One tab per connection, each with its status dot, then the tab that
// connects another. `items` is [{ id, label, mono, dotTone }].
export function Tabs({ label, items, selected, onSelect }) {
    return (
        <KitTabs
            label={label}
            value={selected}
            onChange={onSelect}
            className="max-w-full flex-wrap justify-self-start"
            items={items.map((item) => ({
                id: item.id,
                label: (
                    <span className="inline-flex items-center gap-[7px]">
                        {item.dotTone ? (
                            <span aria-hidden="true" className={`h-[7px] w-[7px] shrink-0 rounded-full ${toneDotClass(item.dotTone)}`} />
                        ) : null}
                        <span className={item.mono ? "font-mono text-[12px]" : ""}>{item.label}</span>
                    </span>
                ),
            }))}
        />
    );
}
